<template>
  <SrpModal v-model:isVisible="isModalVisible">
    <template #content>
      <template v-if="!askUserForPayment">
        <h2 class="global-h2" style="margin-bottom: 10px">On {{ moment(props.subscriptionEndDate).format("MMMM Do, YYYY") }} your subscription will update to a $200/month subscription.</h2>
        It may take up to <b>24 hours</b> for that to be reflected on your account.
      </template>
      <template v-else>
        <h2 class="global-h2">Payment Info Needed</h2>
        <div style="margin: 10px 0">Please add a default payment method before switching to monthly. Thank you!</div>
        <div>If you have questions reach out to <CopyTextNew>support@shrpa.com</CopyTextNew></div>
      </template>
    </template>

    <template #footer>
      <SrpButton @click="!askUserForPayment ? requestSwitchToMonthly() : handleAddPayment()" color="orange" :isDisabled="isLoading || isAddingPayment">
        <template v-if="isLoading || isAddingPayment" #icon>
          <LoadingIcon :size="22" />
        </template>
        {{ !askUserForPayment ? "Switch to Monthly" : isAddingPayment ? "Redirecting..." : "Add Payment" }}
      </SrpButton>
    </template>
  </SrpModal>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from "vue";
import { shrpaApi } from "@repos/axios";
import moment from "moment";

// Components
import SrpModal from "@components/ui/SrpModal.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import CopyTextNew from "@components/CopyTextNew.vue";

const props = withDefaults(
  defineProps<{
    pageId: string;
    subscriptionEndDate: string;
    hasStripeCustomerId: boolean;
  }>(),
  {
    pageId: "",
    subscriptionEndDate: "",
    hasStripeCustomerId: false,
  }
);

const emit = defineEmits<{
  (e: "requestSent"): void;
  (e: "addPayment"): void;
}>();

// Is Modal Visible ===========================================================
const isModalVisible = ref<boolean>(false);
const hasPaymentSetup = ref<boolean>(false);
const askUserForPayment = computed(() => !hasPaymentSetup.value && props.hasStripeCustomerId);

// Check payment setup status =================================================
async function checkPaymentSetup() {
  const response = await shrpaApi.get(`/customers/${props.pageId}/has-payment-setup`);
  hasPaymentSetup.value = response.data;
}

onMounted(() => {
  checkPaymentSetup();
});

// Request switch to monthly ==================================================
const isLoading = ref<boolean>(false);
const isRequestJustSent = ref<boolean>(false);
const isAddingPayment = ref<boolean>(false);

async function requestSwitchToMonthly() {
  isLoading.value = true;

  await shrpaApi.post(`/customers/${props.pageId}/request-switch-to-monthly`);

  isLoading.value = false;
  isRequestJustSent.value = true;

  isModalVisible.value = false;

  emit("requestSent");
}

function handleAddPayment() {
  isAddingPayment.value = true;
  emit("addPayment");
}

// Define expose ==============================================================
defineExpose({
  open: () => (isModalVisible.value = true),
  close: () => (isModalVisible.value = false),
});
</script>

<style scoped lang="scss"></style>
